import { Component, Injector, Input, OnInit } from '@angular/core';

import { BaseFormComponent } from '../base-form.component';
import { CoverpathControllerInterface } from '../../../../interfaces/controller/CoverpathControllerInterface';
import { AmorphicService } from '../../../../services/amorphic/amorphic.service';
import { Subscription } from 'rxjs';
import { AlertIntent } from '../../../shared/alert/alert.component';

@Component({
  moduleId: module.id,
  selector: 'radio-group',
  templateUrl: './radio-group.component.html',
})
export class RadioGroupComponent extends BaseFormComponent implements OnInit {
  @Input() property: string;
  @Input() showTitle: boolean;
  @Input() error?: boolean = false;
  @Input() mask = {
    guide: true,
    mask: [/^([0-1])/, /\d/, '/', /^(1|2)/, /^(9|0|1)/, /\d/, /\d/],
    placeholder: 'MM/YYYY',
  };

  selectedItem: string;
  options: Array<any>;
  radioGroupName: string;
  controller: CoverpathControllerInterface;
  isError: boolean = false;
  stateChangeSubscription: Subscription;
  alertIntent = AlertIntent;

  constructor(protected injector: Injector, private amorphicService: AmorphicService) {
    super(injector);
    this.controller = this.amorphicService.controller;
    this.showTitle = true;
    const applicationChangeProps = [
      'customer.application.primaryPolicy.ownerClassification',
      'customer.application.primaryPolicy.isJointOwnerSomeoneElse',
    ];

    if (applicationChangeProps.includes(this.prop)) {
      this.stateChangeSubscription = this.geoChangeService.geoChangeSource$.subscribe((event) => {
        this.handleGeoChange(event);
      });
    }
  }

  ngOnInit() {
    if (!this.property) {
      this.property = this.questionItem.prop;
      this.property = this.handleIterate(this.property);
    } else if (this.property.includes('[i]')) {
      this.property = this.handleIterate(this.property);
    }

    this.radioGroupName = this.loopIndex > 0 ? this.questionItem.fid + this.loopIndex : this.questionItem.fid;

    super.ngOnInit();

    //if an option is removed from a property and database still holds value which is already removed, it resets the component value and also database
    //do it only if there is responses allowed, as it signifies that option was removed recently
    const selectedItem = this.selectedItem;
    if (
      this.questionItem.responsesAllowed &&
      this.options &&
      this.options.filter((option) => option.value === selectedItem).length === 0
    ) {
      this.resetValue();
      this.resetNode();
      this.enableNestedControls();
    }

    this.updateFromModel();

    if (this.questionItem.sectionValidation) {
      this.isError = !this.isValidProp(this.property, this.selectedItem);
    }

    this.questionAnsweredSubscription = this.appSvc.questionAnsweredObservable$.subscribe(() => {
      if (this.questionItem.sectionValidation) {
        this.isError = !this.isValidProp(this.property, this.selectedItem);
      }
    });
  }

  updateFromModel() {
    this.cdRef.detectChanges();

    this.setOptions();
    const value = this.getValue(this.property) + '';
    this.selectedItem = value;
    this.enableNestedControls();
  }

  setOptions() {
    const prop = this.property;
    const values: any = this.getValuesFor(prop);
    const descriptionsObj = this.getDescriptionsFor(prop);
    if (!this.areOptionsDifferent(values)) {
      return;
    }

    if (this.options) {
      this.options.length = 0;
    } else {
      this.options = [];
    }

    values.forEach((val) => {
      let description = typeof descriptionsObj[val] === 'object' ? descriptionsObj[val].text : descriptionsObj[val];
      description = this.parsePredicates(description);
      this.options.push({
        value: val,
        description: description,
      });
    });
  }

  private areOptionsDifferent(values) {
    if (!this.options || !values) {
      return true;
    }
    if (this.options.length !== values.length) {
      return true;
    }
    let ret = false;
    for (let i = 0; i < this.options.length; i++) {
      ret = ret || this.options[i].value !== values[i];
    }
    return ret;
  }

  getModifierClasses() {
    const classNames = [];
    const vertical = this.questionItem.controlType === 'radio-group-vertical';
    const hasText = this.questionItem.controlType.includes('-text');
    const hasMoreThanThreeOptions = this.options && this.options.length > 3;

    if (vertical || hasText || hasMoreThanThreeOptions) {
      classNames.push('radio-group--vertical');
    } else {
      classNames.push('radio-group--inline');
    }

    if (this.isDisabled) {
      classNames.push('radio-group--disabled');
    }

    if (this.isReadonly) {
      classNames.push('radio-group--readonly');
    }

    return classNames.join(' ');
  }

  enableNestedControls() {
    // find which radio is a text input and remove the __ marking it
    if (
      this.questionItem.controlType === 'radio-group-with-text' ||
      this.questionItem.controlType === 'radio-group-with-text-date'
    ) {
      this.options.forEach((option) => {
        option.isEnabled = option.value === this.selectedItem;
        if (option.description.indexOf('__ Date') !== -1) {
          option.description = option.description.replace('__ Date', '');
          option.isDateInput = true;
        } else if (option.description.indexOf('__') !== -1) {
          const descriptionArr = option.description.split('__');
          // check if theres text before and after the text input
          if (descriptionArr.length === 2) {
            option.beforeInputDescription = descriptionArr[0];
            option.description = descriptionArr[1];
            option.extraClasses = ['small', 'input-sandwich'];
            option.isSandwich = true;
          } else {
            option.beforeInputDescription = descriptionArr[0];
            option.extraClasses = ['small'];
          }

          option.isTextInput = true;
        }
      });
    }
  }

  resetValue() {
    this.selectedItem = '';
  }

  async trigger() {
    // owner/insured questions are changed, make sure Please Acknowledge section is updated before section check
    if (this.questionItem.prop.includes('insuredType') || this.questionItem.prop.includes('ownerType')) {
      this.controller.customer.application.insuredHasAcknowledged = false;
      this.controller.customer.application.ownerHasAcknowledged = false;
      this.controller.customer.application.otherOwnerHasAcknowledged = false;
    }

    if (this.questionItem.prop.includes('whenLastSmokedOrUsedMarijuana')) {
      this.controller.customer.application.primaryPolicy.insured.riskFactors.yearsAgoLastSmokedOrUsedMarijuana = null;
    }

    /**
     * If the owner or joint owner is also the insured, check if the current Insured's state requires an application switch
     *
     * It is possible that the Owner is not also the insured
     * and the user could have switched the insured's state from
     * an ICC state -> a Non ICC state with unique app questions like CA or FL (or vice versa)
     * which requires an application switch in order to render the correct questions
     */
    if (
      (this.prop.includes('ownerClassification') && (this.selectedItem === 'you' || this.selectedItem === 'joint')) ||
      (this.prop.includes('isJointOwnerSomeoneElse') && this.selectedItem === 'false')
    ) {
      // check Insured's state against the "application" state
      const insuredState = this.controller.customer.application.primaryPolicy.insured.address.state;
      const applicationState = this.controller.customer.application.primaryPolicy.getApplicationState();
      const propName = this.prop.split('.').pop();
      const oldPropValue = this.controller.customer.application.primaryPolicy[propName];

      if (await this.unsupportedUseCasesService.isUnsupportedUseCase('insuredState', insuredState)) {
        this.appSvc.markPropertyAsChanged(this.prop);
        return;
      }

      this.geoChangeService.changeBasedOnStateIfNecessary(
        this.property,
        insuredState,
        applicationState,
        oldPropValue,
        this.selectedItem,
        'application-form'
      );
    } else {
      this.isError = !(await this.validateAndSet(this.property, this.selectedItem));
      this.enableNestedControls();
    }
  }

  handleBlur() {
    if (!this.errorMessage[this.property]) {
      if (this.selectedItem === 'null') {
        this.checkEmptyAndValidate(this.property, null);
      } else {
        this.checkEmptyAndValidate(this.property, this.selectedItem);
      }
    }
  }

  private async handleGeoChange(response): Promise<void> {
    /*
        3 possible results from changing property that can cause an application change

        1 - user changes property, is prompted to change app, leaves app
                no further action here, this component is destroyed
                This function is not even invoked
        2 - user changes property, is prompted to change app, chooses to stay
                replace newly selected propery value with original property value
                case: propValueKept
        3 - user changes property, does not require prompt to leave app
                no further action here, this function is not even invoked
        */
    if (response.prop === this.prop) {
      if (response.event === 'propValueChanged') {
        this.isError = !(await this.validateAndSet(this.property, response.value));
        this.enableNestedControls();
      } else if (response.event === 'propValueKept') {
        // reset to old prop value
        this.selectedItem = response.value;
      }
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    if (this.stateChangeSubscription) {
      this.stateChangeSubscription.unsubscribe();
    }
  }
}
